import { useRef, useState, useEffect } from 'react'
import { File, X, Loader2 } from 'lucide-react'
import * as XLSX from 'xlsx'
import { motion, AnimatePresence } from 'framer-motion'
import Select from 'react-select'
import ConfirmModal from '@/widgets/ConfirmModal'
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getClasses, getGroups } from '@/api/kuizy';
import { countryCode } from "../settings/master";

const REQUIRED_FIELDS = ["first_name*", "last_name*", "email*"];
const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const phoneRx = /^[0-9]{6,15}$/
const FIELDS = [
  "first_name*", "last_name*", "email*", "country", "phone",
  "job_title", "birth_year", "classroom_name", "classroom_short_name",
  "group_name", "group_short_name", "classroom_id",
  "group_id",
];

export default function ExcelImportModal({ open, onUpload, onClose }) {
  const fileRef = useRef(null)
  const [file, setFile] = useState(null)
  const [checking, setChecking] = useState(false)
  const [preview, setPreview] = useState(null)
  const [alertMsg, setAlertMsg] = useState('')
  const [group, setGroup] = useState(null)
  const [classrooms, setClassrooms] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    getClasses().then(r => setClassrooms(r.data));
    getGroups().then(r => setGroups(r.data));
  }, []);

  const alert = m => setAlertMsg(m)

  const handleCheck = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setFile(f);
    setChecking(true);

    try {
      const wb = XLSX.read(await f.arrayBuffer(), { type: 'array' });
      // อ่าน Sheet แรก (DataImport)
      const rawData = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });

      const data = rawData.filter(row => {
        // แถวที่จะ "เอาไว้" ต้องมีการกรอก first_name, last_name หรือ email อย่างใดอย่างหนึ่งเป็นอย่างน้อย
        // หรือจะเช็คว่าต้องกรอกครบทั้ง 3 อย่างก็ได้ (แล้วแต่ Business Logic)
        return (
          `${row["first_name*"]}`.trim() !== '' ||
          `${row["last_name*"]}`.trim() !== '' ||
          `${row["email*"]}`.trim() !== ''
        );
      });

      console.log('data from sheet : ', data);

      if (data.length === 0) throw new Error('ไฟล์ไม่มีข้อมูล');

      const header = Object.keys(data[0]);
      console.log(header);

      // ตรวจสอบว่าคอลัมน์หลักอยู่ครบไหม
      if (!REQUIRED_FIELDS.every(c => header.includes(c))) {
        throw new Error(`ไฟล์ไม่ถูกต้อง ต้องมีคอลัมน์: ${REQUIRED_FIELDS.join(', ')}`);
      }

      const errors = [];
      const errCells = [];

      data.forEach((row, idx) => {
        const rowNum = idx + 2;
        const currentErrors = [];

        // 🔥 1. เพิ่มการตรวจสอบว่าแถวนี้ "ว่าง" หรือไม่
        // เราจะถือว่าแถวว่าง ถ้า REQUIRED_FIELDS ทุกช่องไม่มีการกรอกข้อมูลเลย
        const isRowEmpty = REQUIRED_FIELDS.every(k => !`${row[k]}`.trim());

        if (isRowEmpty) return; // ถ้าว่างจริง ให้ skip ข้ามแถวนี้ไปเลย ไม่ต้องบันทึก Error

        // 2. ตรวจสอบ REQUIRED (เฉพาะแถวที่ไม่ว่าง)
        REQUIRED_FIELDS.forEach(k => {
          if (!`${row[k]}`.trim()) {
            currentErrors.push(`ขาดข้อมูล: ${k}`);
            errCells.push({ row: idx, col: k });
          }
        });

        // 3. ตรวจสอบ Email Format
        if (row["email*"] && !emailRx.test(row["email*"])) {
          currentErrors.push('รูปแบบอีเมลไม่ถูกต้อง');
          errCells.push({ row: idx, col: "email*" });
        }

        // 4. ตรวจสอบ Classroom คู่ (Name + Short Name)
        if (row.classroom_name && !`${row.classroom_short_name}`.trim()) {
          currentErrors.push('ถ้ามี Classroom Name ต้องระบุ Classroom Short Name ด้วย');
          errCells.push({ row: idx, col: "classroom_short_name" });
        }

        // 5. ตรวจสอบ Group คู่ (Name + Short Name)
        if (row.group_name && !`${row.group_short_name}`.trim()) {
          currentErrors.push('ถ้ามี Group Name ต้องระบุ Group Short Name ด้วย');
          errCells.push({ row: idx, col: "group_short_name" });
        }

        // ... (ตรวจสอบ Phone และรวม Error เหมือนเดิม)

        if (currentErrors.length > 0) {
          errors.push({
            row: rowNum,
            issue: currentErrors.join(' | ')
          });
        }
      });

      setPreview({
        rows: data.length,
        ok: data.length - errors.length,
        bad: errors.length,
        errors: errors.slice(0, 100), // แสดงแค่ 100 แถวแรกเพื่อ Performance
        fixed: data,
        errCells,
      });

    } catch (err) {
      alert(err.message || 'ไม่สามารถอ่านไฟล์');
    } finally {
      setChecking(false);
    }
  };

  const doUpload = () => {
    if (!preview || preview.ok === 0) return
    console.log('file:', file);
    console.log('preview.fixed:', preview.fixed);
    console.log('preview.errCells:', preview.errCells);
    console.log('group:', group);
    onUpload({ file, fixed: preview.fixed, errCells: preview.errCells, group })
  }

  const groupOptions = groups.map(g => ({ value: g.id, label: g.name }))

  const downloadExcel = async () => {
    // 1. สร้าง Workbook ใหม่
    const workbook = new ExcelJS.Workbook();

    /* ===============================
    Sheet: DataImport
      ================================ */
    const dataSheet = workbook.addWorksheet('DataImport');

    // ใส่ Header และตั้งค่าความกว้างคอลัมน์
    dataSheet.addRow(FIELDS);
    dataSheet.columns = FIELDS.map(() => ({ width: 22 }));

    // Freeze header (แถว 1)
    dataSheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

    // ใส่ Comment / Hint (ExcelJS ใช้รูปแบบ note)
    dataSheet.getCell('A1').note = "ต้องกรอกเสมอ (Required)";
    dataSheet.getCell('H1').note = "เลือกจาก dropdown หรือค้นหาใน sheet Classrooms";
    dataSheet.getCell('J1').note = "เลือกจาก dropdown หรือค้นหาใน sheet Groups";

    /* ===============================
        Sheet: Classrooms (Lookup)
    ================================ */
    const classroomSheet = workbook.addWorksheet('Classrooms');
    classroomSheet.addRow(["classroom_id", "classroom_name", "classroom_short_name"]);
    classrooms.forEach(c => classroomSheet.addRow([c.id, c.name, c.code]));
    classroomSheet.columns = [{ width: 30 }, { width: 25 }, { width: 25 }];
    classroomSheet.autoFilter = 'A1:C1000';

    /* ===============================
        Sheet: Groups (Lookup)
    ================================ */
    const groupSheet = workbook.addWorksheet('Groups');
    groupSheet.addRow(["group_id", "group_name", "group_short_name"]);
    groups.forEach(g => groupSheet.addRow([g.id, g.name, g.code]));
    groupSheet.columns = [{ width: 30 }, { width: 25 }, { width: 25 }];
    groupSheet.autoFilter = 'A1:C1000';



    // --- Data Validation (H2:H1000 และ J2:J1000) ---
    for (let i = 2; i <= 1000; i++) {
      // ======================
      // classroom_name (H)
      // ======================
      const classroomCell = dataSheet.getCell(`H${i}`);
      classroomCell.dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ["'Classrooms'!$B$2:$B$1000"],
        showDropDown: true,
        showErrorMessage: false,
        promptTitle: "Classroom",
        prompt: "เลือกจาก dropdown หรือพิมพ์ชื่อใหม่ได้"
      };

      // 🔥 AUTO FILL classroom_short_name (I)
      const classroomShortCell = dataSheet.getCell(`I${i}`);
      classroomShortCell.value = {
        formula: `IFERROR(VLOOKUP(H${i}, Classrooms!B:C, 2, FALSE), "")`
      };

      // ❗ REQUIRED short_name if name typed
      classroomShortCell.dataValidation = {
        type: 'custom',
        allowBlank: false,
        formulae: [
          `OR(H${i}="", ISNUMBER(MATCH(H${i},Classrooms!$B$2:$B$1000,0)), I${i}<>"")`
        ],
        showErrorMessage: true,
        errorStyle: 'stop',
        errorTitle: 'ข้อมูลไม่ครบ',
        error: 'ถ้าพิมพ์ Classroom Name ใหม่ ต้องกรอก Classroom Short Name ด้วย',
      };

      dataSheet.addConditionalFormatting({
        ref: `I${i}`,
        rules: [
          {
            type: 'expression',
            formulae: [`AND(H${i}<>"", I${i}="", ISERROR(MATCH(H${i},Classrooms!$B$2:$B$1000,0)))`],
            style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFC7CE' } } }, // สีแดงอ่อน
          }
        ]
      });

      dataSheet.addConditionalFormatting({
        ref: `K${i}`,
        rules: [{
          type: 'expression',
          formulae: [`AND(J${i}<>"", K${i}="", ISERROR(MATCH(J${i},Groups!$B$2:$B$1000,0)))`],
          style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFC7CE' } } },
        }]
      });


      // ======================
      // group_name (J)
      // ======================
      const groupCell = dataSheet.getCell(`J${i}`);
      groupCell.dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ["'Groups'!$B$2:$B$1000"],
        showDropDown: true,
        showErrorMessage: false,
        promptTitle: "Group",
        prompt: "เลือกจาก dropdown หรือพิมพ์ชื่อใหม่ได้"
      };

      // 🔥 AUTO FILL group_short_name (K)
      const groupShortCell = dataSheet.getCell(`K${i}`);
      groupShortCell.value = {
        formula: `IFERROR(VLOOKUP(J${i}, Groups!B:C, 2, FALSE), "")`
      };

      const classroomIdCell = dataSheet.getCell(`L${i}`);
      classroomIdCell.value = {
        // ความหมาย: ใน sheet Classrooms ให้เอาค่าจาก Col A (ID) 
        // โดยค้นหาบรรทัดที่ Col B (Name) ตรงกับค่าใน H (classroom_name)
        formula: `IFERROR(INDEX(Classrooms!$A:$A, MATCH(H${i}, Classrooms!$B:$B, 0)), "")`
      };

      // ==========================================
      // 🔥 AUTO FILL group_id (M)
      // ใช้ INDEX + MATCH เพื่อหา ID (Col A) จาก Name (Col B)
      // ==========================================
      const groupIdCell = dataSheet.getCell(`M${i}`);
      groupIdCell.value = {
        formula: `IFERROR(INDEX(Groups!$A:$A, MATCH(J${i}, Groups!$B:$B, 0)), "")`
      };

      dataSheet.getColumn('L').hidden = true;
      dataSheet.getColumn('M').hidden = true;


      const countryCell = dataSheet.getCell(`D${i}`);
      countryCell.dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: ["'Countries'!$C$2:$C$1000"], // dial_code
        showDropDown: true,
        showErrorMessage: false,
        promptTitle: "Country Code",
        prompt: "เลือก country code เช่น +66 หรือพิมพ์เองได้"
      };
    }


    const countrySheet = workbook.addWorksheet('Countries');

    // Header
    countrySheet.addRow([
      "country_name",
      "code",
      "country_code"
    ]);

    // Data
    countryCode.forEach(c => {
      countrySheet.addRow([
        c.name,
        c.code,
        c.dial_code
      ]);
    });

    // Width
    countrySheet.columns = [
      { width: 30 },
      { width: 18 },
      { width: 10 }
    ];

    // Filter (ไว้ค้นหา)
    countrySheet.autoFilter = 'A1:C1000';

    // 🔥 ซ่อน sheet lookup
    classroomSheet.state = 'hidden';
    groupSheet.state = 'hidden';
    countrySheet.state = 'hidden';
    // (optional) ให้ DataImport เป็น sheet แรกที่เปิด
    workbook.views = [
      {
        activeTab: workbook.worksheets.indexOf(dataSheet)
      }
    ];

    ['A1', 'B1', 'C1'].forEach(addr => {
      const cell = dataSheet.getCell(addr);
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFA500' }
      };
    });

    /* ===============================
        Export File
    ================================ */
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "user_import_template.xlsx");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/45 backdrop-blur-[2px] z-40"
          />
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed z-50 inset-x-4 md:inset-x-0 mx-auto max-w-lg w-full bg-white rounded-xl shadow-2xl flex flex-col max-h-[95vh]"
          >
            <header className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <File size={18} />
                นำเข้า Excel
              </h2>
              <button onClick={onClose} className="text-gray-500 hover:text-red-500">
                <X size={20} />
              </button>
            </header>

            <div className="flex flex-col gap-6 px-6 py-5 overflow-y-auto">
              <div>
                <h3 className="font-semibold mb-2">เลือกไฟล์ Excel</h3>
                <div className="space-y-1 text-sm text-gray-600 mb-4">
                  <p>
                    1. ดาวน์โหลด&nbsp;
                    <button
                      onClick={downloadExcel}
                      className="text-primary hover:underline"
                    >
                      Excel_template.xlsx
                    </button>
                  </p>
                  <p>2. กรอกข้อมูลให้ครบถ้วน</p>
                  <p>3. Choose file แล้วกด “ตรวจไฟล์”</p>

                  <p>5. กดปุ่ม "แสดงข้อมูลในตาราง" </p>
                  <p>6. ดับเบิลคลิกเซลล์เพื่อแก้ไข</p> 
                  <p>7. กดปุ่ม "นำเข้าข้อมูล" เพื่อนำเข้าข้อมูลและส่งอีเมลเชิญเข้าเป็นสมากชิก</p>
                  <p>8. ระบบจะแสดงผลการนำเข้า</p>

                </div>
                <div className="flex flex-col sm:flex-row gap-3 border p-3 rounded-lg border-gray-200">
                  <input ref={fileRef} type="file" accept=".xlsx,.xls" className="flex-1 text-primary" />
                  <button
                    onClick={handleCheck}
                    className="bg-primary hover:bg-secondary text-white font-semibold rounded-lg px-4 py-2 text-sm"
                  >
                    ตรวจไฟล์
                  </button>
                </div>
              </div>

              {/* {groups.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">เลือกกลุ่มผู้เรียนที่จะเพิ่มผู้เรียน</h3>
                  <Select
                    classNamePrefix="rs"
                    options={groupOptions}
                    value={groupOptions.find(o => o.value === group)}
                    onChange={o => setGroup(o.value)}
                    isSearchable
                  />
                </div>
              )} */}

              {checking && (
                <div className="flex justify-center">
                  <Loader2 className="animate-spin text-primary" />
                </div>
              )}

              {preview && (
                <div className="space-y-3">
                  <p>
                    รวม <b>{preview.rows}</b> แถว – นำเข้าได้ <b>{preview.ok}</b> แถว
                  </p>
                  {preview.bad > 0 && (
                    <>
                      <p className="text-red-600">ข้าม {preview.bad} แถว:</p>
                      <div className="max-h-52 overflow-auto border rounded-lg">
                        <table className="w-full text-xs table-auto">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="py-1 px-2 w-16 text-left">แถว</th>
                              <th className="py-1 px-2 text-left">ปัญหา</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.errors.map((e, i) => (
                              <tr key={i} className="odd:bg-white even:bg-gray-50">
                                <td className="py-1 px-2">{e.row}</td>
                                <td className="py-1 px-2">{e.issue}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <footer className="flex flex-wrap justify-end gap-3 px-6 py-4 border-t">
              <button
                onClick={onClose}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg px-4 py-2 text-sm"
              >
                ยกเลิก
              </button>
              <button
                onClick={doUpload}
                disabled={!preview || preview.ok === 0}
                className="bg-primary disabled:opacity-40 hover:bg-secondary text-white font-semibold rounded-lg px-4 py-2 text-sm"
              >
                แสดงข้อมูลในตาราง
              </button>
            </footer>
          </motion.div>

          {alertMsg && (
            <ConfirmModal
              type="alert"
              title="แจ้งเตือน"
              message={alertMsg}
              onClose={() => setAlertMsg('')}
            />
          )}
        </>
      )}
    </AnimatePresence>
  )
}
